import { useEffect, useMemo, useRef, useState } from 'react';
import { Box3, Matrix4, Vector3 } from 'three';

import { getSelectionTransformationFrame } from 'app/modules/wallforming/components/scenes/objects/articles/utils/transformationUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { type DraggingBoxWorkerInput, postDraggingBoxTask } from 'app/modules/wallforming/components/workers';

import type { ArticleDragReference } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleDragReference';
import type { ArticleInstance } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleInstance';

interface TransformationBoxData {
    matrix: Matrix4;
    boxSize: Vector3;
    boxCenter: Vector3;
    anchorRotation: number;
}

/**
 * Computes and returns transformation box data for the currently selected article instances.
 */
export const useTransformationMeshData = (): TransformationBoxData | null => {
    const articleInstanceSelections = usePlacementStore((state) => state.articleInstanceSelections);
    const recomputeTransformationBox = usePlacementStore((state) => state.recomputeTransformationBox);
    const activePoiIndex = usePlacementStore((state) => state.poiSelection.current.index);
    const [boxData, setBoxData] = useState<TransformationBoxData | null>(null);

    const selectionKey = useMemo(
        () => Object.keys(articleInstanceSelections).sort().join('|'),
        [articleInstanceSelections]
    );

    const prevSelectionKeyRef = useRef(selectionKey);

    useEffect(() => {
        const state = usePlacementStore.getState();
        const selectionIds = Object.keys(state.articleInstanceSelections);

        if (selectionIds.length === 0) {
            return;
        }

        let isDisposed = false;

        const selectionChanged = prevSelectionKeyRef.current !== selectionKey;
        prevSelectionKeyRef.current = selectionKey;

        // Also recompute if recomputeTransformationBox toggles
        if (selectionChanged || recomputeTransformationBox) {
            queueMicrotask(() => {
                if (!isDisposed) {
                    setBoxData(null);
                }
            });
        }

        const frame = getSelectionTransformationFrame(state);
        if (!frame) {
            return () => {
                isDisposed = true;
            };
        }

        const { position: anchorPosition, alignment: anchorAlignment, rotation: anchorRotation } = frame;

        const articleTypesMap = new Map<string, DraggingBoxWorkerInput['articleTypes'][0]>();
        const instancesForWorker: DraggingBoxWorkerInput['instances'] = [];

        for (const instanceId of selectionIds) {
            const context = resolveSelectedInstanceContext(state, instanceId);
            if (!context?.dragRef) {
                continue;
            }

            const { articleId, instance } = context;

            // Use the merged physical geometry bounding box from the BatchedMesh geometry slots.
            const batchArticleTypeId = `batch::${articleId}`;
            if (!articleTypesMap.has(batchArticleTypeId)) {
                const geometrySlots = state.articleGeometrySlots.get(articleId);
                const { physical: physicalBatch, sheet: sheetBatch } = state.batchedMeshes;
                if (geometrySlots && geometrySlots.physicalSlots.length > 0 && physicalBatch) {
                    const box = new Box3();
                    physicalBatch.getBoundingBoxAt(geometrySlots.physicalSlots[0].slotId, box);

                    // Merge additional physical slots
                    const mergedBox = box.clone();
                    for (let s = 1; s < geometrySlots.physicalSlots.length; s++) {
                        const slotBox = new Box3();
                        physicalBatch.getBoundingBoxAt(geometrySlots.physicalSlots[s].slotId, slotBox);
                        mergedBox.union(slotBox);
                    }

                    // Include sheet slot if present
                    if (geometrySlots.sheetSlotId !== null && sheetBatch) {
                        const sheetBox = new Box3();
                        sheetBatch.getBoundingBoxAt(geometrySlots.sheetSlotId, sheetBox);
                        mergedBox.union(sheetBox);
                    }

                    const identity = new Matrix4();
                    articleTypesMap.set(batchArticleTypeId, {
                        id: batchArticleTypeId,
                        matrixWorld: Array.from(identity.elements),
                        geomMin: { x: mergedBox.min.x, y: mergedBox.min.y, z: mergedBox.min.z },
                        geomMax: { x: mergedBox.max.x, y: mergedBox.max.y, z: mergedBox.max.z }
                    });
                }
            }

            // Use first part's transform for the merged batch geometry
            const firstPart = Object.values(instance.parts)[0];
            if (firstPart && articleTypesMap.has(batchArticleTypeId)) {
                instancesForWorker.push({
                    position: { x: firstPart.position.x, y: firstPart.position.y, z: firstPart.position.z },
                    alignment: { x: firstPart.alignment.x, y: firstPart.alignment.y, z: firstPart.alignment.z },
                    scale: { x: firstPart.scale.x, y: firstPart.scale.y, z: firstPart.scale.z },
                    rotation: firstPart.rotation,
                    articleTypeId: batchArticleTypeId
                });
            }
        }

        if (articleTypesMap.size === 0 || instancesForWorker.length === 0) {
            return () => {
                isDisposed = true;
            };
        }

        postDraggingBoxTask({
            type: 'computeDraggingBox',
            articleTypes: Array.from(articleTypesMap.values()),
            instances: instancesForWorker,
            anchor: {
                position: {
                    x: anchorPosition.x,
                    y: anchorPosition.y,
                    z: anchorPosition.z
                },
                alignment: {
                    x: anchorAlignment.x,
                    y: anchorAlignment.y,
                    z: anchorAlignment.z
                },
                rotation: anchorRotation
            }
        })
            .then((result) => {
                if (isDisposed) {
                    return;
                }

                if (result.isEmpty) {
                    setBoxData(null);
                    return;
                }

                const matrix = new Matrix4().fromArray(result.localToWorld);
                const localMin = new Vector3(result.localMin.x, result.localMin.y, result.localMin.z);
                const localMax = new Vector3(result.localMax.x, result.localMax.y, result.localMax.z);
                const boxSize = new Vector3().subVectors(localMax, localMin);

                const boxCenter = new Vector3().addVectors(localMin, localMax).multiplyScalar(0.5);
                setBoxData({ matrix, boxSize, boxCenter, anchorRotation });
            })
            .catch((e) => {
                if (isDisposed) {
                    return;
                }
                if (e instanceof DOMException && e.name === 'AbortError') {
                    return;
                }
                setBoxData(null);
            });

        return () => {
            isDisposed = true;
        };
    }, [selectionKey, recomputeTransformationBox, activePoiIndex]);

    return useMemo(() => {
        if (!selectionKey || !boxData) {
            return null;
        }

        return boxData;
    }, [boxData, selectionKey]);
};

function resolveSelectedInstanceContext(
    state: ReturnType<typeof usePlacementStore.getState>,
    instanceId: string
): {
    articleId: string;
    instance: ArticleInstance;
    dragRef?: ArticleDragReference;
} | null {
    const dragRef = state.getSelectedArticleDragReference(instanceId);
    if (dragRef?.articleRef.current) {
        const articleId = dragRef.articleRef.current.id;
        const instance = dragRef.articleRef.current.articleInstances[instanceId];
        if (instance) {
            return { articleId, instance, dragRef };
        }
    }

    const articleId = Object.keys(state.articles).find((id) => instanceId in state.articles[id].articleInstances);
    if (!articleId) {
        return null;
    }

    const instance = state.articles[articleId].articleInstances[instanceId];
    if (!instance) {
        return null;
    }

    return { articleId, instance };
}
